/* ============================================================
   BB Card Rail
   BELLA n' BEAR — swipe-gallery for the collection / search cards.

   Drives both engine .bb-cf-card cards (upgraded here) and native
   server-rendered cards. Image source resilient: imgs[] then img + img2.

   DESKTOP hover -> swipe model (hover:hover + pointer:fine only):
     - HOVER: crossfade image 1 -> image 2 via the .bb-rail__hover
       overlay (opacity only, no motion) after a short hover-intent
       delay. Once the fade settles, the rail is silently primed to
       slide 1 (image 2) underneath the overlay.
     - FIRST SWIPE / NEXT: the rail already sits on image 2, so the
       first advance lands on image 3 (hover already "spent" 1 -> 2).
       The overlay is dropped INSTANTLY (not faded) so it can't ghost
       over the rail as it slides to image 3.
     - LEAVE: crossfade back to image 1 with the same opacity fade as
       the hover.

   MOBILE (no hover): the overlay is never created and swipe is the
   normal 1 -> 2 -> 3 rail scroll — the desktop controller is gated off.

   ------------------------------------------------------------------
   LAZY SLIDES (perf — the reason this file was last changed)
   ------------------------------------------------------------------
   The rail used to emit slides 1..n with a real src/srcset and merely
   loading="lazy". That does NOT stop the download: the slides sit in a
   horizontally-scrolling container that is INSIDE the viewport, so the
   browser treats them as "near viewport" and fetches them immediately.

   Measured on /collections/shop-all: the index island advertises 3
   images per product, so 24 cards = 72 rail images fetched on load
   (plus one hover overlay per card on desktop) — for pictures the
   shopper cannot see until they swipe. Worse, one of those unseen
   slides (a 23KB shot on card 2) was being picked as the LCP element,
   with ~1.4-2.0s of "resource load delay", because it is only created
   when this script runs at DOM-ready and therefore cannot benefit from
   the <head> preload (snippets/bb-lcp-preload.liquid).

   Now: slide 0 keeps its real src/srcset (it is the SAME url as the
   server <img> and the LCP preload, so it is already in cache — no
   second download, no flash). Slides 1..n are emitted with data-src /
   data-srcset and NO src, so nothing is fetched. They are hydrated on
   the first real intent to browse that card: hover, touch, horizontal
   wheel, or a nav/dot click. The desktop hover overlay is likewise
   created lazily on first hover instead of eagerly for every card.

   IMAGE SPEC: slide 0's src/srcset/sizes are kept BYTE-IDENTICAL to the
   server <img> (snippets/bb-cf-card.liquid) and the LCP preload
   (snippets/bb-lcp-preload.liquid) — 400 src; 200/400/600/768/1080
   srcset; 25vw / 33vw / 46vw sizes. If any of the three changes, change
   ALL THREE, or the browser discards the preload and re-downloads.

   Structural CSS (rail container / slides / nav / dots / hover overlay)
   ships RENDER-BLOCKING in assets/bb-collection-cards.css so the cards are
   sized on first paint (no CLS). This file no longer injects styles.
   ============================================================ */
(function () {
  'use strict';

  var SIZES = '(min-width:990px) 25vw, (min-width:750px) 33vw, 46vw';
  var ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"></polyline></svg>';

  function esc(s) {
    return (s == null ? '' : String(s))
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function setW(url, w) {
    if (!url) return url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + w);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function imgsFor(p) {
    if (!p) return [];
    var out = [], i;
    var focals = p.focals || [];
    if (p.imgs && p.imgs.length) {
      for (i = 0; i < p.imgs.length; i++) { if (p.imgs[i]) out.push({ u: p.imgs[i], f: focals[i] || '' }); }
    }
    if (out.length < 2) {
      out = [];
      if (p.img) out.push({ u: p.img, f: focals[0] || '' });
      if (p.img2 && p.img2 !== p.img) out.push({ u: p.img2, f: focals[1] || '' });
    }
    return out;
  }

  function buildMap() {
    var map = {};
    var list = null;
    if (window.BBCollectionFilter && window.BBCollectionFilter.products && window.BBCollectionFilter.products.length) {
      list = window.BBCollectionFilter.products;
    } else {
      var island = document.getElementById('bb-cf-data');
      if (island) {
        try { list = (JSON.parse(island.textContent) || {}).products; } catch (e) { list = null; }
      }
    }
    if (!list) return map;
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      if (!p || p.id == null) continue;
      var arr = imgsFor(p);
      if (arr.length) map[String(p.id)] = arr;
    }
    return map;
  }

  function srcsetFor(u) {
    return setW(u, 200) + ' 200w, ' + setW(u, 400) + ' 400w, ' + setW(u, 600) + ' 600w, ' + setW(u, 768) + ' 768w, ' + setW(u, 1080) + ' 1080w';
  }

  function railHTML(imgs, title) {
    var slides = '', dots = '', shown = 0;
    for (var i = 0; i < imgs.length; i++) {
      var item = imgs[i];
      var u = item && item.u;
      if (!u) continue;
      var f = (item && item.f) || '';
      var ss = srcsetFor(u);
      if (shown === 0) {
        // Slide 0: real src/srcset. Byte-identical to the server <img> and the
        // <head> LCP preload, so this is a cache hit — no second download.
        slides += '<div class="bb-rail__slide"><img class="bb-rail__img" style="--focal-point: ' + esc(f) + ';" src="' + esc(setW(u, 400)) +
          '" srcset="' + esc(ss) + '" sizes="' + SIZES + '" alt="' + esc(title) + '" decoding="async"></div>';
      } else {
        // Slides 1..n: NO src/srcset — nothing is fetched until the shopper
        // shows intent to browse this card (see hydrateRail).
        slides += '<div class="bb-rail__slide"><img class="bb-rail__img" style="--focal-point: ' + esc(f) + ';" data-src="' + esc(setW(u, 400)) +
          '" data-srcset="' + esc(ss) + '" sizes="' + SIZES + '" alt="" loading="lazy" decoding="async"></div>';
      }
      dots += '<span class="bb-rail__dot' + (shown === 0 ? ' is-active' : '') + '" data-idx="' + shown + '"></span>';
      shown++;
    }
    return '<div class="bb-rail" data-bb-rail>' + slides + '</div>' +
      '<button type="button" class="bb-rail__nav bb-rail__nav--prev" aria-hidden="true" tabindex="-1">' + ARROW + '</button>' +
      '<button type="button" class="bb-rail__nav bb-rail__nav--next" aria-hidden="true" tabindex="-1">' + ARROW + '</button>' +
      '<span class="bb-rail__dots" aria-hidden="true">' + dots + '</span>';
  }

  /* Promote the deferred slides of ONE card to real images. Idempotent and
     cheap; safe to call on every hover/touch. */
  function hydrateRail(media) {
    if (!media || media.dataset.bbHydrated === '1') return;
    media.dataset.bbHydrated = '1';
    var imgs = media.querySelectorAll('img.bb-rail__img[data-src]');
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i];
      var ss = im.getAttribute('data-srcset');
      if (ss) im.setAttribute('srcset', ss);
      im.setAttribute('src', im.getAttribute('data-src'));
      im.removeAttribute('data-src');
      im.removeAttribute('data-srcset');
    }
  }

  function upgrade(card, map) {
    if (card.dataset.bbRail) return;
    var id = card.getAttribute('data-product-id');
    var imgs = id ? map[String(id)] : null;
    if (!imgs || imgs.length < 2) { card.dataset.bbRail = 'skip'; return; }
    var media = card.querySelector('.bb-cf-media');
    if (!media) { card.dataset.bbRail = 'skip'; return; }
    var titleEl = card.querySelector('.bb-cf-title');
    var title = titleEl ? titleEl.textContent : '';
    media.classList.add('bb-cf-media--rail');
    media.innerHTML = railHTML(imgs, title);
    card.dataset.bbRail = '1';
  }

  function upgradeAll(scope) {
    var cards = scope.querySelectorAll('.bb-cf-card:not([data-bb-rail])');
    if (!cards.length) return;
    var map = buildMap();
    for (var i = 0; i < cards.length; i++) upgrade(cards[i], map);
  }

  function desktop() {
    return !!(window.matchMedia && window.matchMedia('(hover:hover) and (pointer:fine)').matches);
  }

  /* Crossfade-on-hover overlay, created LAZILY for one card on first hover.
     Previously this ran for every card at upgrade time, which downloaded a
     second full image per card on desktop for nothing. */
  function ensureHoverImage(media) {
    if (!desktop()) return;
    if (!media || media.getAttribute('data-bb-hover') === '1') return;
    var imgs = media.querySelectorAll('.bb-rail__img');
    if (imgs.length < 2) return;
    media.setAttribute('data-bb-hover', '1');
    var second = imgs[1];
    var ov = document.createElement('img');
    ov.className = 'bb-rail__hover';
    ov.src = second.getAttribute('src') || '';
    var ss = second.getAttribute('srcset'); if (ss) ov.setAttribute('srcset', ss);
    var sz = second.getAttribute('sizes'); if (sz) ov.setAttribute('sizes', sz);
    ov.setAttribute('alt', '');
    ov.setAttribute('aria-hidden', 'true');
    ov.setAttribute('decoding', 'async');
    applyOvFocal(ov, second);
    media.appendChild(ov);
  }

  function slideW(rail) { return rail.clientWidth || rail.offsetWidth || 1; }
  function imgAt(media, i) { var a = media.querySelectorAll('.bb-rail__img'); return a[i] || null; }
  function overlayOf(media) { return media.querySelector('.bb-rail__hover'); }
  function imgSrc(img) { return img ? (img.currentSrc || img.getAttribute('src') || '') : ''; }
  function applyOvFocal(ov, img) {
    var f = img && img.style ? img.style.getPropertyValue('--focal-point') : '';
    ov.style.objectPosition = f || 'center center';
  }

  function scrollRail(rail, idx) {
    if (!rail) return;
    rail.scrollTo({ left: idx * slideW(rail), behavior: 'smooth' });
  }

  function beginBrowse(media) {
    var st = media._bbHover;
    if (!st) return;
    st.mode = 'browsing';
    var ov = overlayOf(media);
    if (ov) { ov.style.transition = 'none'; ov.style.opacity = '0'; }
  }

  function beginBrowseFromPreview(media, rail) {
    var st = media._bbHover;
    if (!st || st.mode !== 'preview') return;
    if (!st.primed) { rail.scrollLeft = slideW(rail); st.primed = true; st.basePos = slideW(rail); }
    beginBrowse(media);
  }

  function bindGrid(grid) {
    grid.addEventListener('click', function (e) {
      var nav = e.target.closest && e.target.closest('.bb-rail__nav');
      if (nav) {
        e.preventDefault();
        e.stopPropagation();
        var media = nav.closest('.bb-cf-media--rail');
        var rail = media && media.querySelector('[data-bb-rail]');
        if (!rail) return;
        hydrateRail(media); // intent to browse -> load the rest of this card
        var w = slideW(rail);
        beginBrowseFromPreview(media, rail);
        rail.scrollBy({ left: nav.classList.contains('bb-rail__nav--next') ? w : -w, behavior: 'smooth' });
        return;
      }
      var dot = e.target.closest && e.target.closest('.bb-rail__dot');
      if (dot) {
        e.preventDefault();
        e.stopPropagation();
        var media2 = dot.closest('.bb-cf-media--rail');
        var rail2 = media2 && media2.querySelector('[data-bb-rail]');
        if (!rail2) return;
        hydrateRail(media2);
        beginBrowseFromPreview(media2, rail2);
        scrollRail(rail2, parseInt(dot.getAttribute('data-idx'), 10) || 0);
      }
    }, true);

    grid.addEventListener('scroll', function (e) {
      var rail = e.target;
      if (!rail || !rail.classList || !rail.classList.contains('bb-rail')) return;
      var media = rail.closest('.bb-cf-media--rail');
      if (!media) return;
      hydrateRail(media); // the shopper is swiping this card
      var w = slideW(rail);
      var idx = Math.round(rail.scrollLeft / w);
      var dots = media.querySelectorAll('.bb-rail__dot');
      for (var d = 0; d < dots.length; d++) dots[d].classList.toggle('is-active', d === idx);
      var st = media._bbHover;
      if (st && st.mode === 'preview' && Math.abs(rail.scrollLeft - st.basePos) > 8) {
        beginBrowse(media);
      }
    }, true);

    /* Pre-empt the overlay hide the INSTANT a horizontal swipe begins, and use
       the same signal to hydrate the card's remaining slides. */
    function preemptSwipe(target) {
      var media = target && target.closest && target.closest('.bb-cf-media--rail');
      if (!media) return;
      hydrateRail(media);
      var st = media._bbHover;
      if (!st || st.mode !== 'preview') return;
      var rail = media.querySelector('[data-bb-rail]');
      if (rail) beginBrowseFromPreview(media, rail);
    }
    grid.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      preemptSwipe(e.target);
    }, { passive: true, capture: true });
    grid.addEventListener('touchstart', function (e) {
      preemptSwipe(e.target);
    }, { passive: true, capture: true });
  }

  /* Desktop hover -> swipe state machine. Gated to fine-pointer hover
     devices; on touch this never runs and the overlay is never created. */
  function bindHoverDesktop(grid) {
    if (!desktop()) return;

    grid.addEventListener('mouseover', function (e) {
      var item = e.target.closest && e.target.closest('.product-grid__item');
      if (!item) return;
      if (e.relatedTarget && item.contains(e.relatedTarget)) return;
      var media = item.querySelector('.bb-cf-media--rail');
      if (!media) return;

      // First hover on this card: load its remaining slides, then build the
      // crossfade overlay from slide 1. Both are lazy by design.
      hydrateRail(media);
      ensureHoverImage(media);

      var rail = media.querySelector('[data-bb-rail]');
      var ov = overlayOf(media);
      if (!rail || !ov) return;
      var st = media._bbHover || (media._bbHover = { mode: null, primed: false, basePos: 0, timers: {} });
      if (st.timers.leave) { clearTimeout(st.timers.leave); st.timers.leave = null; }
      if (st.mode === 'preview' || st.mode === 'browsing') return;
      if (st.timers.enter) return;
      st.timers.enter = setTimeout(function () {
        st.timers.enter = null;
        if (st.mode === 'browsing') return;
        st.mode = 'preview';
        st.primed = false;
        st.basePos = 0;
        var second = imgAt(media, 1);
        if (second) {
          ov.src = imgSrc(second);
          var ss = second.getAttribute('srcset'); if (ss) ov.srcset = ss;
          applyOvFocal(ov, second);
        }
        ov.style.transition = 'opacity 0.4s ease';
        ov.style.opacity = '1';
        st.timers.prime = setTimeout(function () {
          st.timers.prime = null;
          if (st.mode !== 'preview') return;
          rail.scrollLeft = slideW(rail);
          st.primed = true;
          st.basePos = slideW(rail);
        }, 430);
      }, 120);
    });

    grid.addEventListener('mouseout', function (e) {
      var item = e.target.closest && e.target.closest('.product-grid__item');
      if (!item) return;
      if (e.relatedTarget && item.contains(e.relatedTarget)) return;
      var media = item.querySelector('.bb-cf-media--rail');
      if (!media) return;
      var st = media._bbHover;
      if (!st) return;
      var rail = media.querySelector('[data-bb-rail]');
      var ov = overlayOf(media);
      if (!rail || !ov) return;
      if (st.timers.enter) { clearTimeout(st.timers.enter); st.timers.enter = null; }
      if (st.timers.prime) { clearTimeout(st.timers.prime); st.timers.prime = null; }
      var mode = st.mode;
      st.mode = null;
      st.primed = false;
      if (mode === 'browsing') {
        var first = imgAt(media, 0);
        if (first) {
          ov.src = imgSrc(first);
          var fss = first.getAttribute('srcset'); if (fss) ov.srcset = fss; else ov.removeAttribute('srcset');
          applyOvFocal(ov, first);
        }
        ov.style.transition = 'none';
        ov.style.opacity = '0';
        void ov.offsetWidth;
        ov.style.transition = 'opacity 0.4s ease';
        ov.style.opacity = '1';
        st.timers.leave = setTimeout(function () {
          st.timers.leave = null;
          rail.scrollLeft = 0;
          ov.style.transition = 'none';
          ov.style.opacity = '0';
          var second = imgAt(media, 1);
          if (second) {
            ov.src = imgSrc(second);
            var ss2 = second.getAttribute('srcset'); if (ss2) ov.srcset = ss2;
            applyOvFocal(ov, second);
          }
        }, 430);
      } else {
        rail.scrollLeft = 0;
        ov.style.transition = 'opacity 0.4s ease';
        ov.style.opacity = '0';
      }
    });
  }

  function init() {
    var root = document.querySelector('[data-bb-cf-root]');
    if (!root) return;
    var grid = root.querySelector('ul.product-grid');
    if (!grid) return;

    bindGrid(grid);
    bindHoverDesktop(grid);

    var scheduled = false;
    // NOTE: no addHoverImage() here any more — the overlay is built on first
    // hover (ensureHoverImage) instead of eagerly for every card.
    var run = function () { scheduled = false; upgradeAll(grid); };
    var schedule = function () {
      if (scheduled) return;
      scheduled = true;
      (window.requestAnimationFrame || window.setTimeout)(run);
    };

    schedule();

    var mo = new MutationObserver(schedule);
    mo.observe(grid, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();